/**
 * DrawingCheckForm — create + complete + amend for CR-2026-DC-FINAL-CHECK
 * (D-032 §5.2). Drawing-revision final-check release gate per PDF §15.2.
 *
 * Redesigned 2026-05-05 (Phase 2.6, Class P) onto the PaperForm helper
 * library.
 */

import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import ApiCall from "../../utils/apicall";
import {
  Stack, Typography, Button, Alert, Chip, Box, Grid,
  TextField, MenuItem, Breadcrumbs, Link as MuiLink, IconButton,
  FormControlLabel, Checkbox, Divider,
  Table, TableHead, TableBody, TableRow, TableCell,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import {
  HomeRounded, ArrowBackRounded, CheckCircleOutlineRounded,
  CancelRounded, EditRounded, AddRounded, RuleFolderOutlined,
} from "@mui/icons-material";

import {
  getCheck, createCheck, completeCheck, rejectCheck, amendCheck, addExceptions,
  CHECK_AREAS, CHECK_AREA_LABELS, EXCEPTION_AREAS, EXCEPTION_SEVERITY, allowedCheckTransitions,
} from "../../utils/drawingChecksApi";
import {
  PaperFormCard, PaperFormHeader, PaperFormSection, PaperFormFooter,
  FieldLabel,
} from "../../components/PaperForm";

export default function DrawingCheckForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [params] = useSearchParams();
  const isCreate = !id || id === "new";

  const [dc, setDc] = useState(null);
  const [exceptions, setExceptions] = useState([]);
  const [documentId, setDocumentId] = useState(params.get("document_id") || "");
  const [jobId, setJobId] = useState(params.get("job_id") || "");
  const [drawingRev, setDrawingRev] = useState(params.get("rev") || "");
  const [checkerUserId, setCheckerUserId] = useState("");
  const [checklistVersion, setChecklistVersion] = useState("v1.0");
  const [sourceType, setSourceType] = useState("IN_HOUSE");
  const [detailerVendorId, setDetailerVendorId] = useState("");
  const [externalFirmName, setExternalFirmName] = useState("");
  const [externalCheckerName, setExternalCheckerName] = useState("");
  const [externalCheckedAt, setExternalCheckedAt] = useState("");
  const [vendors, setVendors] = useState([]);

  const [areas, setAreas] = useState(() => {
    const init = {};
    CHECK_AREAS.forEach((a) => { init[a] = false; });
    return init;
  });
  const [notes, setNotes] = useState("");

  const [loading, setLoading] = useState(!isCreate);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const [openReject, setOpenReject] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [openException, setOpenException] = useState(false);
  const [excDraft, setExcDraft] = useState({ area: "GEOMETRY", severity: "MINOR", description: "" });

  const refresh = () => {
    if (isCreate) return;
    setLoading(true);
    getCheck(id)
      .then((data) => {
        setDc(data);
        setExceptions(data.exceptions || []);
        const next = {};
        CHECK_AREAS.forEach((a) => { next[a] = !!data[a]; });
        setAreas(next);
        setNotes(data.notes || "");
      })
      .catch((err) => setError(err?.response?.data?.error || err?.message))
      .finally(() => setLoading(false));
  };
  useEffect(refresh, [id, isCreate]);
  useEffect(() => {
    if (!isCreate) return;
    ApiCall.get("/vendor", { params: { avl_status: "approved" } })
      .then((res) => setVendors(res.data?.data?.vendors || []))
      .catch(() => setVendors([]));
  }, [isCreate]);

  const handleCreate = async () => {
    setError(null);
    if (!documentId || !drawingRev || !checkerUserId) {
      setError("Drawing No., Drawing Rev., and Checked By (User No.) are all required.");
      return;
    }
    if (sourceType === "OUTSOURCED") {
      if (!detailerVendorId && !externalFirmName.trim()) {
        setError("Outsourced detailing requires an approved AVL vendor or a detailing firm name.");
        return;
      }
      if (!externalCheckerName.trim()) {
        setError("Outsourced detailing requires the external checker's name.");
        return;
      }
    }
    setBusy(true);
    try {
      const created = await createCheck({
        document_id: Number(documentId),
        job_id: jobId ? Number(jobId) : null,
        drawing_revision: drawingRev,
        checker_user_id: Number(checkerUserId),
        checklist_version: checklistVersion,
        source_type: sourceType,
        detailer_vendor_id: sourceType === "OUTSOURCED" && detailerVendorId ? Number(detailerVendorId) : null,
        external_firm_name: sourceType === "OUTSOURCED" ? (externalFirmName.trim() || null) : null,
        external_checker_name: sourceType === "OUTSOURCED" ? (externalCheckerName.trim() || null) : null,
        external_checked_at: sourceType === "OUTSOURCED" ? (externalCheckedAt || null) : null,
      });
      navigate(`/drawing-checks/${created.id}`);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message);
    } finally { setBusy(false); }
  };

  const handleComplete = async () => {
    setBusy(true); setError(null);
    try { await completeCheck(id, areas); refresh(); }
    catch (err) { setError(err?.response?.data?.error || err?.message); }
    finally { setBusy(false); }
  };

  const handleReject = async () => {
    setBusy(true); setError(null);
    try {
      await rejectCheck(id, rejectReason);
      setOpenReject(false); setRejectReason(""); refresh();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message);
    } finally { setBusy(false); }
  };

  const handleAmend = async () => {
    const reason = window.prompt("Amend reason (min 4 chars):");
    if (!reason) return;
    setBusy(true); setError(null);
    try { await amendCheck(id, reason); refresh(); }
    catch (err) { setError(err?.response?.data?.error || err?.message); }
    finally { setBusy(false); }
  };

  const handleAddException = async () => {
    if (!excDraft.description || excDraft.description.length < 4) {
      setError("Exception description (min 4 chars) required.");
      return;
    }
    setBusy(true); setError(null);
    try {
      await addExceptions(id, [excDraft]);
      setOpenException(false);
      setExcDraft({ area: "GEOMETRY", severity: "MINOR", description: "" });
      refresh();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message);
    } finally { setBusy(false); }
  };

  const result = dc?.result || "IN_PROGRESS";
  const next = isCreate ? [] : allowedCheckTransitions(result);
  const canEditAreas = !isCreate && (result === "IN_PROGRESS" || result === "AMENDED");
  const allAreasChecked = CHECK_AREAS.every((a) => areas[a]);

  const headerNumber = dc?.id ? `#${dc.id}` : (isCreate ? "Assigned automatically" : `#${id}`);
  const headerDate = useMemo(() => {
    if (dc?.created_at) return new Date(dc.created_at).toLocaleDateString();
    return new Date().toLocaleDateString();
  }, [dc]);

  if (loading) {
    return (
      <Box sx={{ pt: 6, textAlign: "center" }}>
        <Typography color="text.secondary">Loading Final Check…</Typography>
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>{isCreate ? "New Final Check" : `Final Check #${id}`} | SFM</title>
      </Helmet>

      <Box sx={{ bgcolor: "#fafafa", minHeight: "100vh" }}>
        <Box sx={{ maxWidth: "md", mx: "auto", pt: 3, px: { xs: 2, md: 3 } }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Breadcrumbs>
              <MuiLink href="/" color="inherit" underline="hover">
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <HomeRounded fontSize="small" /><span>Home</span>
                </Stack>
              </MuiLink>
              <MuiLink href="/drawing-checks" color="inherit" underline="hover">
                Drawing Final Checks
              </MuiLink>
              <Typography color="text.primary">{isCreate ? "New" : `#${id}`}</Typography>
            </Breadcrumbs>
            <IconButton onClick={() => navigate("/drawing-checks")} aria-label="Back">
              <ArrowBackRounded />
            </IconButton>
          </Stack>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {!isCreate && result === "ACCEPTED" && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <strong>Accepted</strong> on {dc.accepted_at ? new Date(dc.accepted_at).toISOString().slice(0, 10) : ""}.
              This drawing is cleared for release for fabrication (Drawing No. {dc.document_id}).
            </Alert>
          )}
          {!isCreate && result === "REJECTED" && dc?.rejection_reason && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <strong>Rejection reason:</strong> {dc.rejection_reason}
            </Alert>
          )}
        </Box>

        <PaperFormCard>
          <PaperFormHeader
            formId="SFM · Drawing Final Check"
            title={isCreate ? "New Drawing Final Check" : "Drawing Final Check"}
            subtitle="AISC Drawing Control §5.2 · 7-area drawing check before release for fabrication"
            status={result}
            number={headerNumber}
            numberLabel="Check No."
            date={headerDate}
            icon="rule_folder"
          />

          {/* TARGET DOCUMENT IDENTIFICATION */}
          <Grid container spacing={4} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <PaperFormSection label="Drawing" bottomPadding={0}>
                <Box>
                  <FieldLabel required>Drawing No.</FieldLabel>
                  <TextField
                    fullWidth size="small" type="number"
                    value={documentId}
                    onChange={(e) => setDocumentId(e.target.value)}
                    disabled={!isCreate}
                  />
                </Box>
                <Box>
                  <FieldLabel hint="optional — filled in automatically if the drawing is tied to a job">
                    Job No.
                  </FieldLabel>
                  <TextField
                    fullWidth size="small" type="number"
                    value={jobId}
                    onChange={(e) => setJobId(e.target.value)}
                    disabled={!isCreate}
                  />
                </Box>
                <Box>
                  <FieldLabel required>Drawing Rev.</FieldLabel>
                  <TextField
                    fullWidth size="small"
                    value={drawingRev}
                    onChange={(e) => setDrawingRev(e.target.value)}
                    disabled={!isCreate}
                    placeholder='e.g., "C"'
                  />
                </Box>
              </PaperFormSection>
            </Grid>

            <Grid item xs={12} md={6}>
              <PaperFormSection label="Checked By" bottomPadding={0}>
                <Box>
                  <FieldLabel required>User No.</FieldLabel>
                  <TextField
                    fullWidth size="small" type="number"
                    value={checkerUserId || (dc ? dc.checker_user_id : "")}
                    onChange={(e) => setCheckerUserId(e.target.value)}
                    disabled={!isCreate}
                  />
                </Box>
                <Box>
                  <FieldLabel hint="the checklist version sets which checks apply">
                    Checklist Version
                  </FieldLabel>
                  <TextField
                    fullWidth size="small"
                    value={checklistVersion}
                    onChange={(e) => setChecklistVersion(e.target.value)}
                    disabled={!isCreate}
                  />
                </Box>
              </PaperFormSection>
            </Grid>
          </Grid>

          {isCreate && (
          <Grid container spacing={4} sx={{ mb: 4 }}>
            <Grid item xs={12}>
              <PaperFormSection label="Detailing Source" hint="who produced the drawings — in-house or an outsourced detailer">
                <Box>
                  <FieldLabel>Source</FieldLabel>
                  <TextField select fullWidth size="small" value={sourceType} onChange={(e) => setSourceType(e.target.value)}>
                    <MenuItem value="IN_HOUSE">In-house detailing</MenuItem>
                    <MenuItem value="OUTSOURCED">Outsourced detailer</MenuItem>
                  </TextField>
                </Box>
                {sourceType === "OUTSOURCED" && (
                  <>
                    <Box>
                      <FieldLabel hint="approved AVL detailing vendor (preferred)">Detailing Firm (AVL)</FieldLabel>
                      <TextField select fullWidth size="small" value={detailerVendorId} onChange={(e) => setDetailerVendorId(e.target.value)}>
                        <MenuItem value=""><em>— Not in AVL / use free-text below —</em></MenuItem>
                        {vendors.map((v) => (<MenuItem key={v.id} value={v.id}>{v.vendor_name}</MenuItem>))}
                      </TextField>
                    </Box>
                    {!detailerVendorId && (
                      <Box>
                        <FieldLabel hint="if the firm is not an approved vendor yet">Detailing Firm (free-text)</FieldLabel>
                        <TextField fullWidth size="small" value={externalFirmName} onChange={(e) => setExternalFirmName(e.target.value)} placeholder="Firm name" />
                      </Box>
                    )}
                    <Box>
                      <FieldLabel required>External Checker Name</FieldLabel>
                      <TextField fullWidth size="small" value={externalCheckerName} onChange={(e) => setExternalCheckerName(e.target.value)} placeholder="Who performed the outsourced check" />
                    </Box>
                    <Box>
                      <FieldLabel hint="optional · date the external check was performed">External Check Date</FieldLabel>
                      <TextField type="date" fullWidth size="small" InputLabelProps={{ shrink: true }} value={externalCheckedAt} onChange={(e) => setExternalCheckedAt(e.target.value)} />
                    </Box>
                    <Alert severity="info" sx={{ mt: 1 }}>
                      The internal <strong>Checked By</strong> user above is the accountable counter-signer who accepts the outsourced work for release.
                    </Alert>
                  </>
                )}
              </PaperFormSection>
            </Grid>
          </Grid>
          )}

          {/* CHECKLIST — edit mode only */}
          {!isCreate && (
            <>
              <Divider sx={{ mb: 4 }} />
              <PaperFormSection
                label="Checklist (AISC Drawing Control §5.2)"
                hint="all 7 areas must be checked before you can Accept"
              >
                <Box
                  sx={{
                    p: 2, border: "1px solid", borderColor: "divider",
                    borderRadius: 1, bgcolor: "#fafafa",
                  }}
                >
                  <Stack>
                    {CHECK_AREAS.map((a) => (
                      <FormControlLabel
                        key={a}
                        sx={{ m: 0 }}
                        control={
                          <Checkbox
                            size="small"
                            checked={!!areas[a]}
                            disabled={!canEditAreas}
                            onChange={(e) => setAreas({ ...areas, [a]: e.target.checked })}
                          />
                        }
                        label={
                          <Typography variant="body2" sx={{ fontSize: 13 }}>
                            {CHECK_AREA_LABELS[a]}
                          </Typography>
                        }
                      />
                    ))}
                  </Stack>
                </Box>
                {!allAreasChecked && canEditAreas && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    All seven areas must be checked before this check can be Accepted (release-eligible).
                  </Alert>
                )}
              </PaperFormSection>

              <PaperFormSection label="Notes" hint="optional · context, anomalies, follow-ups">
                <TextField
                  fullWidth multiline rows={3} size="small"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  disabled={!canEditAreas}
                />
              </PaperFormSection>

              {/* EXCEPTIONS */}
              <PaperFormSection
                label={`Exceptions (${exceptions.length})`}
                hint="minor findings tracked separately — they don\u2019t block acceptance"
              >
                {canEditAreas && (
                  <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
                    <Button
                      startIcon={<AddRounded />}
                      onClick={() => setOpenException(true)}
                      size="small"
                      sx={{ textTransform: "none" }}
                    >
                      Add exception
                    </Button>
                  </Stack>
                )}
                {exceptions.length === 0 ? (
                  <Box
                    sx={{
                      p: 2.5, textAlign: "center",
                      border: "1px dashed", borderColor: "divider", borderRadius: 2,
                      bgcolor: "#fafafa",
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      No exceptions recorded.
                    </Typography>
                  </Box>
                ) : (
                  <Table
                    size="small"
                    sx={{
                      "& th": {
                        fontSize: 11, fontWeight: 700, letterSpacing: "0.06em",
                        textTransform: "uppercase", color: "text.secondary",
                        bgcolor: "#fafafa",
                      },
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell>Area</TableCell>
                        <TableCell>Severity</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Resolution</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {exceptions.map((e) => (
                        <TableRow key={e.id}>
                          <TableCell>{e.area}</TableCell>
                          <TableCell>
                            <Chip
                              size="small" label={e.severity}
                              color={e.severity === "CRITICAL" ? "error" : e.severity === "MAJOR" ? "warning" : "default"}
                            />
                          </TableCell>
                          <TableCell>{e.description}</TableCell>
                          <TableCell>
                            {e.resolution || (e.resolved_at ? "(resolved)" : <em style={{ color: "#dc3545" }}>open</em>)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </PaperFormSection>
            </>
          )}

          <PaperFormFooter
            citation="AISC Drawing Control Ch. 7/8/9 §5.2 — final drawing check required before release for fabrication"
          />

          {/* ACTION BAR */}
          <Stack direction="row" justifyContent="flex-end" spacing={1} flexWrap="wrap" sx={{ mt: 4 }}>
            {!isCreate && (
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ flex: 1 }}>
                {next.includes("ACCEPTED") && (
                  <Button
                    startIcon={<CheckCircleOutlineRounded />} variant="contained" color="success" size="small"
                    disabled={busy || !allAreasChecked} onClick={handleComplete}
                    title={!allAreasChecked ? "Tick every checklist area before accepting" : ""}
                    sx={{ textTransform: "none", borderRadius: 2 }}
                  >Accept (clear for release)</Button>
                )}
                {next.includes("REJECTED") && (
                  <Button
                    startIcon={<CancelRounded />} variant="outlined" color="error" size="small"
                    onClick={() => setOpenReject(true)} disabled={busy}
                    sx={{ textTransform: "none", borderRadius: 2 }}
                  >Reject</Button>
                )}
                {next.includes("AMENDED") && result !== "AMENDED" && (
                  <Button
                    startIcon={<EditRounded />} variant="outlined" size="small"
                    onClick={handleAmend} disabled={busy}
                    sx={{ textTransform: "none", borderRadius: 2 }}
                  >Amend</Button>
                )}
              </Stack>
            )}

            {isCreate && (
              <>
                <Box sx={{ flex: 1 }} />
                <Button
                  variant="text" onClick={() => navigate("/drawing-checks")} disabled={busy}
                  sx={{ color: "text.secondary" }}
                >Cancel</Button>
                <Button
                  variant="contained" startIcon={<RuleFolderOutlined />}
                  onClick={handleCreate} disabled={busy}
                  sx={{ px: 3, borderRadius: 2, textTransform: "none", fontWeight: 600 }}
                >
                  {busy ? "Creating…" : "Start Final Check"}
                </Button>
              </>
            )}
          </Stack>
        </PaperFormCard>
      </Box>

      <Dialog open={openReject} onClose={() => setOpenReject(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: "error.main" }}>Reject final check</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <FieldLabel required hint="captured in audit_log">
              Rejection Reason (min 4 chars)
            </FieldLabel>
            <TextField
              fullWidth size="small" multiline rows={3}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenReject(false)} disabled={busy}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleReject}
            disabled={busy || rejectReason.length < 4}>Reject</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openException} onClose={() => setOpenException(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add exception</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box>
              <FieldLabel required>Area</FieldLabel>
              <TextField
                select fullWidth size="small"
                value={excDraft.area}
                onChange={(e) => setExcDraft({ ...excDraft, area: e.target.value })}
              >
                {EXCEPTION_AREAS.map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
              </TextField>
            </Box>
            <Box>
              <FieldLabel required>Severity</FieldLabel>
              <TextField
                select fullWidth size="small"
                value={excDraft.severity}
                onChange={(e) => setExcDraft({ ...excDraft, severity: e.target.value })}
              >
                {EXCEPTION_SEVERITY.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
            </Box>
            <Box>
              <FieldLabel required>Description (min 4 chars)</FieldLabel>
              <TextField
                fullWidth size="small" multiline rows={3}
                value={excDraft.description}
                onChange={(e) => setExcDraft({ ...excDraft, description: e.target.value })}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenException(false)} disabled={busy}>Cancel</Button>
          <Button variant="contained" onClick={handleAddException}
            disabled={busy || excDraft.description.length < 4}>Add</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
